/**
 * Decodes a JWT token and returns the parsed payload
 * @param {string} token - The JWT token
 * @returns {object|null} - The decoded payload or null if parsing fails
 */
export function decodeJwt(token) {
    try {
        if (!token || typeof token !== 'string') {
            return null;
        }

        const parts = token.split('.');
        if (parts.length !== 3) {
            return null;
        }

        const payload = parts[1];
        // Add padding if needed
        const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
        const decoded = atob(paddedPayload);
        return JSON.parse(decoded);
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to decode JWT:', error);
        return null;
    }
}

/**
 * Formats a JWT payload for display
 * @param {object} payload - The decoded JWT payload
 * @returns {string} - Formatted JSON string
 */
export function formatJwtPayload(payload) {
    if (!payload) {
        return 'Unable to parse token';
    }

    // Convert timestamps to readable dates
    const formatted = { ...payload };
    if (formatted.iat) {
        formatted.iat = `${formatted.iat} (${new Date(formatted.iat * 1000).toISOString()})`;
    }
    if (formatted.exp) {
        formatted.exp = `${formatted.exp} (${new Date(formatted.exp * 1000).toISOString()})`;
    }
    if (formatted.nbf) {
        formatted.nbf = `${formatted.nbf} (${new Date(formatted.nbf * 1000).toISOString()})`;
    }

    return JSON.stringify(formatted, null, 2);
}

/**
 * Extracts key claims for validation
 * @param {object} payload - The decoded JWT payload
 * @returns {object} - Object with key claims
 */
export function getKeyClaims(payload) {
    if (!payload) {
        return null;
    }

    return {
        sub: payload.sub || 'N/A',
        aud: payload.aud || 'N/A',
        iss: payload.iss || 'N/A',
        scope: payload.scope || 'N/A',
        'extension_FHIRUser': payload.extension_FHIRUser || 'N/A',
        'fhir_user': payload.fhir_user || 'N/A',
        exp: payload.exp ? new Date(payload.exp * 1000).toISOString() : 'N/A',
    };
}
